/**
 * customFieldSlice — CRUD de campos customizáveis por projeto (tipos: text, number, date, select, etc.)
 *
 * Consumidores: DadosPesquisa, GrupoPage (via driveProjectStore)
 * Dependências: googleDrive, helpers
 * Persistência: Google Drive (metadata.json do projeto — campo customFields)
 */
import { googleDrive } from '../../services/googleDrive';
import { ensureAuthenticated } from './helpers';
import { logger } from '../../utils/logger';
import { driveErrorToast } from '../../utils/driveErrorToast';
import { auditLog } from '../../utils/auditLog';
import type { ProjectField, FieldValue, SliceSet, SliceGet } from './types';

export const createCustomFieldSlice = (set: SliceSet, get: SliceGet) => {
  // Usado pelo _backgroundRevalidateCustomFields para evitar sobrescrever dado local
  // mais recente com dado stale vindo do Drive.
  let _lastMutationAt = 0;
  let _isRevalidating = false;

  // Revalidação silenciosa — busca estado fresco do Drive sem bloquear a UI.
  // Chamada quando o cache está stale (>5min) mas já temos dados para servir.
  const _backgroundRevalidateCustomFields = (projectId: string) => {
    if (_isRevalidating) return;
    _isRevalidating = true;
    const startedAt = Date.now();

    ensureAuthenticated().then(() =>
      googleDrive.readProjectJson(projectId)
    ).then(metadata => {
      if (get().activeProjectId !== projectId) { _isRevalidating = false; return; }
      if (_lastMutationAt > startedAt) {
        _isRevalidating = false;
        logger.info('customFieldSlice', 'Revalidação descartada — mutação local mais recente');
        return;
      }
      const freshFields = (metadata?.customFields || []).sort(
        (a: ProjectField, b: ProjectField) => (a.order ?? 0) - (b.order ?? 0)
      );
      set({ cachedCustomFields: freshFields, customFieldsLoadedAt: Date.now() });
      _isRevalidating = false;
    }).catch(err => {
      _isRevalidating = false;
      logger.error('[customFieldSlice] Revalidação em background falhou:', String(err));
    });
  };

  return {
  // ============================================
  // FUNÇÕES DE CAMPOS CUSTOMIZÁVEIS (FASE 7)
  // ============================================

  // Carrega campos customizáveis do projeto ativo
  loadActiveProjectCustomFields: async () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      set({ cachedCustomFields: [], customFieldsLoaded: true });
      return [];
    }

    // TTL: se fresco (<5min) → cache direto
    const loadedAt = get().customFieldsLoadedAt;
    if (get().customFieldsLoaded && loadedAt && Date.now() - loadedAt < 300_000) {
      return get().cachedCustomFields || [];
    }

    // Stale mas com dados → servir cache + revalidar em background
    if (get().customFieldsLoaded && (get().cachedCustomFields || []).length > 0) {
      _backgroundRevalidateCustomFields(activeProjectId);
      return get().cachedCustomFields;
    }

    try {
      await ensureAuthenticated();
      const metadata = await googleDrive.readProjectJson(activeProjectId);
      const fields = metadata.customFields || [];
      // Ordenar por order
      const sortedFields = fields.sort((a: ProjectField, b: ProjectField) => (a.order ?? 0) - (b.order ?? 0));
      set({ cachedCustomFields: sortedFields, customFieldsLoaded: true, customFieldsLoadedAt: Date.now() });
      return sortedFields;
    } catch (error) {
      logger.error('DriveProjectStore', 'Erro ao carregar campos customizáveis:', error);
      set({ cachedCustomFields: [], customFieldsLoaded: true });
      return [];
    }
  },

  // Adiciona campo customizável ao projeto
  addCustomFieldToProject: async (field: Omit<ProjectField, 'id' | 'createdAt' | 'order'>) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Verificar se já existe campo com mesmo nome
    const existingFields = get().cachedCustomFields;
    if (existingFields.some(f => f.name.toLowerCase() === field.name.toLowerCase())) {
      throw new Error('Já existe um campo com este nome');
    }

    // Criar novo campo
    const newField: ProjectField = {
      ...field,
      id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      order: existingFields.length,
      createdAt: new Date().toISOString()
    };

    const updatedFields = [...existingFields, newField];
    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      const metadata = await googleDrive.readProjectJson(activeProjectId);
      await googleDrive.updateProjectJson(activeProjectId, {
        ...metadata,
        customFields: updatedFields
      });

      // Drive confirmou — atualizar cache local
      set({ cachedCustomFields: updatedFields, customFieldsLoadedAt: Date.now() });

      auditLog({ operation: 'create', target: 'campo-customizado', targetId: newField.id, detail: `Campo criado: ${field.name}`, success: true });
      return newField.id;
    } catch (error) {
      logger.error('[customFieldSlice.addCustomField]', error);
      driveErrorToast(error, 'Não foi possível criar o campo. Tente novamente.');
      auditLog({ operation: 'create', target: 'campo-customizado', detail: `Falha ao criar campo: ${field.name}`, success: false, error: String(error) });
      return '';
    }
  },

  // Atualiza campo customizável
  updateCustomFieldInProject: async (fieldId: string, updates: Partial<ProjectField>) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    const existingFields = get().cachedCustomFields;
    const fieldIndex = existingFields.findIndex(f => f.id === fieldId);
    if (fieldIndex === -1) {
      throw new Error('Campo não encontrado');
    }

    // Verificar nome duplicado (se estiver alterando o nome)
    if (updates.name) {
      const duplicate = existingFields.some(f =>
        f.id !== fieldId && f.name.toLowerCase() === updates.name!.toLowerCase()
      );
      if (duplicate) {
        throw new Error('Já existe um campo com este nome');
      }
    }

    const updatedFields = existingFields.map(f =>
      f.id === fieldId ? { ...f, ...updates, updatedAt: new Date().toISOString() } : f
    );
    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      const metadata = await googleDrive.readProjectJson(activeProjectId);
      await googleDrive.updateProjectJson(activeProjectId, {
        ...metadata,
        customFields: updatedFields
      });

      // Drive confirmou — atualizar cache local
      set({ cachedCustomFields: updatedFields, customFieldsLoadedAt: Date.now() });
      auditLog({ operation: 'update', target: 'campo-customizado', targetId: fieldId, detail: `Campo atualizado: ${Object.keys(updates).join(', ')}`, success: true });
    } catch (error) {
      logger.error('[customFieldSlice.updateCustomField]', error);
      driveErrorToast(error, 'Não foi possível atualizar o campo. Tente novamente.');
      auditLog({ operation: 'update', target: 'campo-customizado', targetId: fieldId, detail: `Falha ao atualizar campo`, success: false, error: String(error) });
    }
  },

  // Remove campo customizável do projeto
  removeCustomFieldFromProject: async (fieldId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    const existingFields = get().cachedCustomFields;
    const updatedFields = existingFields
      .filter(f => f.id !== fieldId)
      .map((f, index) => ({ ...f, order: index }));
    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      const metadata = await googleDrive.readProjectJson(activeProjectId);
      await googleDrive.updateProjectJson(activeProjectId, {
        ...metadata,
        customFields: updatedFields
      });

      // Drive confirmou — remover valores deste campo de todos os dados
      const researchData = get().cachedResearchData;
      const updatedData = researchData.map(d => {
        if (d.fieldValues) {
          return {
            ...d,
            fieldValues: d.fieldValues.filter((fv: FieldValue) => fv.fieldId !== fieldId)
          };
        }
        return d;
      });

      // Atualizar cache local
      set({
        cachedCustomFields: updatedFields,
        cachedResearchData: updatedData,
        customFieldsLoadedAt: Date.now()
      });
      auditLog({ operation: 'delete', target: 'campo-customizado', targetId: fieldId, detail: `Campo removido`, success: true });
    } catch (error) {
      logger.error('[customFieldSlice.removeCustomField]', error);
      driveErrorToast(error, 'Não foi possível remover o campo. Tente novamente.');
      auditLog({ operation: 'delete', target: 'campo-customizado', targetId: fieldId, detail: `Falha ao remover campo`, success: false, error: String(error) });
    }
  },

  // Reordena campos customizáveis
  reorderCustomFieldsInProject: async (fieldIds: string[]) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    const existingFields = get().cachedCustomFields;
    const reorderedFields = fieldIds.map((id, index) => {
      const field = existingFields.find(f => f.id === id);
      return field ? { ...field, order: index } : null;
    }).filter(Boolean) as ProjectField[];
    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      const metadata = await googleDrive.readProjectJson(activeProjectId);
      await googleDrive.updateProjectJson(activeProjectId, {
        ...metadata,
        customFields: reorderedFields
      });

      // Drive confirmou — atualizar cache local
      set({ cachedCustomFields: reorderedFields, customFieldsLoadedAt: Date.now() });
      auditLog({ operation: 'update', target: 'campo-customizado', detail: `Campos reordenados`, success: true });
    } catch (error) {
      logger.error('[customFieldSlice.reorderCustomFields]', error);
      driveErrorToast(error, 'Não foi possível reordenar os campos. Tente novamente.');
      auditLog({ operation: 'update', target: 'campo-customizado', detail: `Falha ao reordenar campos`, success: false, error: String(error) });
    }
  },

  // Adiciona opção a campo de seleção
  addOptionToSelectField: async (fieldId: string, option: string) => {
    const existingFields = get().cachedCustomFields;
    const field = existingFields.find(f => f.id === fieldId);

    if (!field) {
      throw new Error('Campo não encontrado');
    }
    if (field.type !== 'select') {
      throw new Error('Este campo não é do tipo seleção');
    }
    if (field.options?.includes(option)) {
      throw new Error('Esta opção já existe');
    }

    const updatedOptions = [...(field.options || []), option];
    await get().updateCustomFieldInProject(fieldId, { options: updatedOptions });

  },

  // Remove opção de campo de seleção
  removeOptionFromSelectField: async (fieldId: string, option: string) => {
    const existingFields = get().cachedCustomFields;
    const field = existingFields.find(f => f.id === fieldId);

    if (!field) {
      throw new Error('Campo não encontrado');
    }
    if (field.type !== 'select') {
      throw new Error('Este campo não é do tipo seleção');
    }

    const updatedOptions = (field.options || []).filter(o => o !== option);
    await get().updateCustomFieldInProject(fieldId, { options: updatedOptions });

    // Limpar valores que usavam esta opção nos dados
    const activeProjectId = get().activeProjectId;
    if (activeProjectId) {
      const researchData = get().cachedResearchData;
      for (const data of researchData) {
        const fieldValue = data.fieldValues?.find((fv: FieldValue) => fv.fieldId === fieldId);
        if (fieldValue && fieldValue.value === option) {
          await get().setFieldValueInData(data.id, fieldId, null);
        }
      }
    }

  },

  // Retorna campos do cache (síncrono)
  getCachedCustomFields: () => {
    return get().cachedCustomFields || [];
  },

  // Invalida o cache de campos
  invalidateCustomFieldsCache: () => {
    set({ cachedCustomFields: [], customFieldsLoaded: false, customFieldsLoadedAt: null });
  },

  // ============================================
  // FUNÇÕES DE VALORES DE CAMPOS (FASE 7)
  // ============================================

  // Define valor de um campo em um dado específico
  setFieldValueInData: async (dataId: string, fieldId: string, value: string | number | boolean | null) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Verificar se o campo existe
    const field = get().cachedCustomFields.find(f => f.id === fieldId);
    if (!field) {
      throw new Error('Campo não encontrado');
    }

    // Buscar dado atual
    const currentData = (get().cachedResearchData || []).find(d => d.id === dataId);
    if (!currentData) {
      throw new Error('Dado não encontrado');
    }

    // Atualizar ou criar valor do campo
    const existingValues: FieldValue[] = currentData.fieldValues || [];
    const valueIndex = existingValues.findIndex(fv => fv.fieldId === fieldId);

    let updatedValues: FieldValue[];
    if (value === null || value === '' || value === undefined) {
      // Remover valor se for nulo/vazio
      updatedValues = existingValues.filter(fv => fv.fieldId !== fieldId);
    } else if (valueIndex >= 0) {
      // Atualizar valor existente
      updatedValues = existingValues.map(fv =>
        fv.fieldId === fieldId ? { ...fv, value } : fv
      );
    } else {
      // Criar novo valor
      updatedValues = [...existingValues, { fieldId, value }];
    }

    _lastMutationAt = Date.now();

    try {
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { fieldValues: updatedValues });

      // Drive confirmou — atualizar cache local
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, fieldValues: updatedValues } : d
        ),
        customFieldsLoadedAt: Date.now()
      }));
      auditLog({ operation: 'update', target: 'valor-campo', targetId: `${dataId}/${fieldId}`, detail: `Valor de campo atualizado`, success: true });
    } catch (error) {
      logger.error('[customFieldSlice.setFieldValue]', error);
      driveErrorToast(error, 'Não foi possível salvar o valor do campo. Tente novamente.');
      auditLog({ operation: 'update', target: 'valor-campo', targetId: `${dataId}/${fieldId}`, detail: `Falha ao salvar valor de campo`, success: false, error: String(error) });
    }
  },

  // Define valor de um campo para múltiplos dados (ação em lote)
  setFieldValuesBatch: async (dataIds: string[], fieldId: string, value: string | number | boolean | null) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Verificar se o campo existe
    const field = get().cachedCustomFields.find(f => f.id === fieldId);
    if (!field) {
      throw new Error('Campo não encontrado');
    }

    // Atualizar cada dado
    for (const dataId of dataIds) {
      try {
        await get().setFieldValueInData(dataId, fieldId, value);
      } catch (error) {
        logger.error('DriveProjectStore', `Erro ao atualizar dado ${dataId}:`, error);
      }
    }

  },

  // Retorna valores de campos para um dado específico (síncrono)
  getFieldValuesForData: (dataId: string) => {
    const data = (get().cachedResearchData || []).find(d => d.id === dataId);
    return data?.fieldValues || [];
  },

  // Revalida campos customizáveis se stale — entry para visibilitychange
  revalidateCustomFieldsIfStale: () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) return;
    if (!get().customFieldsLoaded) return;
    const loadedAt = get().customFieldsLoadedAt;
    if (loadedAt && Date.now() - loadedAt < 300_000) return;
    _backgroundRevalidateCustomFields(activeProjectId);
  },
  };
};
